import * as React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useBudget, useBudgets, useDeleteBudget } from '@/hooks/useBudgets';
import { useCreateTransaction, useTransactions, useTransactionsForBudgetTree, useMoveTransaction } from '@/hooks/useTransactions';
import { useAttachTagToTransaction } from '@/hooks/useTags';
import { useBudgetPermissions } from '@/hooks/useShares';
import { useFxRate } from '@/hooks/useFxRates';
import { useFxRateUtils } from '@/hooks/useFxRates';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { LoadingOverlay } from '@/components/ui/Loading';
import { TransactionList } from '@/components/transaction/TransactionList';
import { TransactionForm } from '@/components/transaction/TransactionForm';
import { RecurringTransactionDialog } from '@/components/transaction/RecurringTransactionDialog';
import { TransactionsSectionHeader } from '@/components/transaction/TransactionsSectionHeader';
import { NoTransactionsEmpty } from '@/components/ui/EmptyState';
import { BudgetCard } from '@/components/budget/BudgetCard';
import { CopyBudgetDialog } from '@/components/budget/CopyBudgetDialog';
import { BudgetOverviewSection } from '@/components/budget/BudgetOverviewSection';
import { SelectionSummaryRibbon } from '@/components/budget/SelectionSummaryRibbon';
import { TransactionSelectionSummaryRibbon } from '@/components/transaction/TransactionSelectionSummaryRibbon';
import { Input } from '@/components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { MoveTransactionDialog } from '@/components/transaction/MoveTransactionDialog';
import { TransactionService } from '@/services/TransactionService';
import { cn, formatDate } from '@/lib/utils';
import type { Budget, CreateTransactionRequest, Transaction, UpdateTransactionRequest } from '@/types';
import { BudgetShareDialog } from '@/components/sharing/BudgetShareDialog';
import { SharedBudgetBadge } from '@/components/sharing/SharingUi';
import { toast } from 'sonner';

function flattenBudgetTree(nodes: Budget[] | undefined): Budget[] {
  if (!nodes?.length) return [];
  return nodes.flatMap((node) => [node, ...flattenBudgetTree(node.children)]);
}

export function BudgetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: budget, isLoading } = useBudget(id);
  const { data: budgets = [] } = useBudgets();
  const { data: parentBudget = null } = useBudget(budget?.parentId ?? undefined);

  const deleteBudget = useDeleteBudget();
  const createTransaction = useCreateTransaction();
  const attachTag = useAttachTagToTransaction();
  const moveTransaction = useMoveTransaction();
  const { canEdit, canDelete, canManageShares, canCopy } = useBudgetPermissions(id, budget);
  const showCopyAction = Boolean(canCopy && budget && !budget.parentId);
  const showActions = canEdit || showCopyAction;
  const { getCommonCurrencies } = useFxRateUtils();

  const { data: fxRate, isLoading: fxRateLoading } = useFxRate(
    budget?.currency ?? '',
    parentBudget?.currency ?? ''
  );

  const convertedAmountToParent = React.useMemo(() => {
    if (!parentBudget || parentBudget.currency === budget?.currency) return null;
    if (!fxRate) return null;
    return budget?.amount ? budget.amount * fxRate : null;
  }, [budget?.amount, budget?.currency, fxRate, parentBudget]);

  const [showAddTransaction, setShowAddTransaction] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = React.useState('');
  const [includeChildTransactions, setIncludeChildTransactions] = React.useState(false);
  const [showCopyDialog, setShowCopyDialog] = React.useState(false);
  const [showShareDialog, setShowShareDialog] = React.useState(false);
  const [showRecurringDialog, setShowRecurringDialog] = React.useState(false);
  const [selectedTagIdsForCreate, setSelectedTagIdsForCreate] = React.useState<string[]>([]);
  const [transactionContextMenu, setTransactionContextMenu] = React.useState<{
    x: number;
    y: number;
    transaction: Transaction;
  } | null>(null);
  const [moveTransactionTarget, setMoveTransactionTarget] = React.useState<Transaction | null>(null);
  // Ordered ids preserve Subtract first − second
  const [selectionMode, setSelectionMode] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [txSelectionMode, setTxSelectionMode] = React.useState(false);
  const [selectedTxIds, setSelectedTxIds] = React.useState<string[]>([]);
  const CONFIRM_WORD = 'DELETE';

  const exitSelectionMode = React.useCallback(() => {
    setSelectionMode(false);
    setSelectedIds([]);
  }, []);

  const exitTxSelectionMode = React.useCallback(() => {
    setTxSelectionMode(false);
    setSelectedTxIds([]);
  }, []);

  const handleToggleSelect = React.useCallback((budgetId: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(budgetId)) {
        const next = prev.filter((selectedId) => selectedId !== budgetId);
        if (next.length === 0) {
          setSelectionMode(false);
        }
        return next;
      }
      return [...prev, budgetId];
    });
  }, []);

  const handleEnterSelection = React.useCallback((budgetId: string) => {
    setTxSelectionMode(false);
    setSelectedTxIds([]);
    setSelectionMode(true);
    setSelectedIds([budgetId]);
  }, []);

  const handleToggleTxSelect = React.useCallback((transactionId: string) => {
    setSelectedTxIds((prev) => {
      if (prev.includes(transactionId)) {
        const next = prev.filter((id) => id !== transactionId);
        if (next.length === 0) {
          setTxSelectionMode(false);
        }
        return next;
      }
      return [...prev, transactionId];
    });
  }, []);

  const handleEnterTxSelection = React.useCallback((transactionId?: string) => {
    setSelectionMode(false);
    setSelectedIds([]);
    setTxSelectionMode(true);
    setSelectedTxIds(transactionId ? [transactionId] : []);
  }, []);

  // Reset dialog/selection state when navigating between budget detail pages (React Router
  // reuses the same component instance for the same route pattern).
  React.useEffect(() => {
    setShowDeleteConfirm(false);
    setDeleteConfirmText('');
    setSelectionMode(false);
    setSelectedIds([]);
    setTxSelectionMode(false);
    setSelectedTxIds([]);
  }, [id]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (txSelectionMode) {
        exitTxSelectionMode();
      } else if (selectionMode) {
        exitSelectionMode();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectionMode, txSelectionMode, exitSelectionMode, exitTxSelectionMode]);

  const selectableBudgets = React.useMemo(
    () => flattenBudgetTree(budget?.children),
    [budget?.children]
  );

  const selectedBudgets: Budget[] = React.useMemo(() => {
    const byId = new Map(selectableBudgets.map((b) => [b.id, b]));
    return selectedIds
      .map((selectedId) => byId.get(selectedId))
      .filter((b): b is Budget => b !== undefined);
  }, [selectableBudgets, selectedIds]);

  const hasChildren = (budget?.children?.length ?? 0) > 0;
  const childCount = budget?.children?.length ?? 0;

  const { data: directTransactions = [], isLoading: directTransactionsLoading } =
    useTransactions(includeChildTransactions ? undefined : budget?.id);

  const { data: treeTransactions = [], isLoading: treeTransactionsLoading } =
    useTransactionsForBudgetTree(
      includeChildTransactions ? budget?.id : undefined,
      true
    );

  const aggregatedTransactions = includeChildTransactions ? treeTransactions : directTransactions;
  const aggregatedTransactionsLoading = includeChildTransactions
    ? treeTransactionsLoading
    : directTransactionsLoading;

  const displayTransactions = React.useMemo(() => {
    if (!budget) return [];
    if (includeChildTransactions) {
      return aggregatedTransactions;
    }
    return aggregatedTransactions;
  }, [aggregatedTransactions, includeChildTransactions, budget]);

  const canSelectTransactions = displayTransactions.length >= 2;

  React.useEffect(() => {
    if (!canSelectTransactions && txSelectionMode) {
      exitTxSelectionMode();
      return;
    }
    setSelectedTxIds((prev) => {
      if (prev.length === 0) return prev;
      const visible = new Set(displayTransactions.map((tx) => tx.id));
      const next = prev.filter((id) => visible.has(id));
      if (next.length === 0 && txSelectionMode) {
        setTxSelectionMode(false);
      }
      return next.length === prev.length ? prev : next;
    });
  }, [canSelectTransactions, txSelectionMode, exitTxSelectionMode, displayTransactions]);

  const selectedTransactions: Transaction[] = React.useMemo(() => {
    const byId = new Map(displayTransactions.map((tx) => [tx.id, tx]));
    return selectedTxIds
      .map((selectedId) => byId.get(selectedId))
      .filter((tx): tx is Transaction => tx !== undefined);
  }, [displayTransactions, selectedTxIds]);

  const handleAddTransaction = async (
    data: CreateTransactionRequest | UpdateTransactionRequest,
    tagIds: string[]
  ) => {
    if (!budget) return;
    const transactionData = {
      ...data,
      budgetId: budget.id,
    } as CreateTransactionRequest;

    const created = await createTransaction.mutateAsync(transactionData);

    if (tagIds.length > 0) {
      // Sequential: each attach bumps transaction @Version.
      for (const tagId of tagIds) {
        await attachTag.mutateAsync({ transactionId: created.id, tagId });
      }
    }

    setSelectedTagIdsForCreate([]);
    setShowAddTransaction(false);
  };

  const handleTransactionContextMenu = React.useCallback(
    (transaction: Transaction, x: number, y: number) => {
      if (!canEdit && !canSelectTransactions) return;
      setTransactionContextMenu({ x, y, transaction });
    },
    [canEdit, canSelectTransactions]
  );

  if (isLoading) {
    return <LoadingOverlay message="Loading budget..." />;
  }

  if (!budget) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <h1 className="text-xl font-semibold text-foreground">Budget not found</h1>
        <p className="text-muted-foreground mt-2">
          This budget may have been deleted or you don't have access.
        </p>
        <Link to="/budgets">
          <Button variant="outline" className="mt-4">
            Back to Budgets
          </Button>
        </Link>
      </div>
    );
  }

  const isObligation = budget.type === 'OBLIGATION';
  const isObligationPaid = isObligation && (budget.spent ?? 0) >= budget.amount;
  const unallocated = budget.unallocated;
  const sumRollover = (nodes: Budget[] | undefined): number => {
    if (!nodes?.length) return 0;
    return nodes.reduce(
      (total, node) => total + (node.rolloverAmount ?? 0) + sumRollover(node.children),
      0
    );
  };
  const collectRolloverChildren = (nodes: Budget[] | undefined): Budget[] => {
    if (!nodes?.length) return [];
    return nodes.flatMap((node) => {
      const own = (node.rolloverAmount ?? 0) > 0 ? [node] : [];
      return [...own, ...collectRolloverChildren(node.children)];
    });
  };
  const childRolloverTotal = sumRollover(budget.children);
  const rolloverChildren = collectRolloverChildren(budget.children);
  const ownRolloverAmount = budget.rolloverAmount ?? 0;
  const totalRolloverAmount = ownRolloverAmount + childRolloverTotal;
  const budgetAmountWithChildRollover = budget.amount + childRolloverTotal;
  const effectiveRemaining =
    (budget.remaining ?? (budget.amount - (budget.spent ?? 0))) + totalRolloverAmount;
  const effectivePercentSpent =
    budget.amount + totalRolloverAmount > 0
      ? ((budget.spent ?? 0) / (budget.amount + totalRolloverAmount)) * 100
      : 0;

  const handleMarkPaid = async () => {
    try {
      await createTransaction.mutateAsync({
        budgetId: budget.id,
        amount: budget.amount,
        currency: budget.currency,
        date: new Date().toISOString().split('T')[0],
        note: 'Obligation payment',
        merchant: undefined,
        category: undefined,
      });
      toast.success('Marked as paid');
    } catch (error: any) {
      toast.error(error?.message ?? 'Failed to mark as paid');
    }
  };

  const handleDelete = async () => {
    await deleteBudget.mutateAsync({
      id: budget.id,
      confirmation: hasChildren,
    });
    setShowDeleteConfirm(false);
    setDeleteConfirmText('');

    if (budget.parentId) {
      navigate(`/budgets/${budget.parentId}`);
    } else {
      navigate('/dashboard');
    }
  };

  const handleSubmitRecurring = async (transactions: CreateTransactionRequest[]) => {
    await Promise.all(
      transactions.map((transaction) => createTransaction.mutateAsync(transaction))
    );
    toast.success(`Added ${transactions.length} transactions`);
  };

  const handleTransactionDrop = async (transactionId: string, targetBudgetId: string) => {
    // Prefer the in-memory list; fall back to fetch so drops work when the
    // source row is not currently listed (e.g. child tx while include-children is off).
    let tx: Transaction | undefined | null = aggregatedTransactions.find((t) => t.id === transactionId);
    if (!tx) {
      tx = await TransactionService.getById(transactionId);
    }
    if (!tx || tx.budgetId === targetBudgetId) return;
    try {
      await moveTransaction.moveAsync(tx, targetBudgetId);
      toast.success('Transaction moved');
    } catch {
      toast.error('Failed to move transaction');
    }
  };

  const handleMoveTransaction = async (targetBudgetId: string) => {
    if (!moveTransactionTarget) return;
    try {
      await moveTransaction.moveAsync(moveTransactionTarget, targetBudgetId);
      toast.success('Transaction moved');
      setMoveTransactionTarget(null);
    } catch {
      toast.error('Failed to move transaction');
      throw new Error('Failed to move transaction');
    }
  };

  const clampMenuPosition = (x: number, y: number) => {
    const menuWidth = 180;
    const menuHeight = 48;
    const padding = 8;
    return {
      left: Math.min(Math.max(padding, x), window.innerWidth - menuWidth - padding),
      top: Math.min(Math.max(padding, y), window.innerHeight - menuHeight - padding),
    };
  };

  return (
    <div className="space-y-6 min-w-0 overflow-x-hidden">
      {/* Sticky back + budget name — stays visible while scrolling (mobile has no top Header) */}
      <div className="sticky top-0 z-30 -mx-4 border-b border-border/80 bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/90 md:top-14 md:mx-0 md:rounded-lg md:border md:px-3">
        <div className="flex items-start justify-between gap-2 min-w-0">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                if (budget.parentId) {
                  navigate(`/budgets/${budget.parentId}`);
                } else {
                  navigate('/dashboard');
                }
              }}
              className="shrink-0 text-muted-foreground hover:text-foreground"
              aria-label="Back"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-foreground truncate md:text-2xl">
              {budget.name}
            </h1>
          </div>
          <div className="flex items-center gap-2 mt-1 pl-7">
            {!budget.parentId && (
              <span className="text-sm text-muted-foreground">
                {formatDate(budget.startDate)} - {formatDate(budget.endDate)}
              </span>
            )}
            <SharedBudgetBadge shared={budget.shared} owned={budget.owned} />
          </div>
        </div>
        
        {showActions && (
          <div className="flex gap-1 shrink-0">
            {showCopyAction && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCopyDialog(true)}
                title="Copy budget"
                aria-label="Copy budget"
                className="px-2 md:px-3"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="md:mr-1"
                >
                  <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                </svg>
                <span className="hidden md:inline">Copy Budget</span>
              </Button>
            )}
            {canEdit && (
              <>
                {canManageShares && !budget.parentId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowShareDialog(true)}
                    title="Share budget"
                    aria-label="Share budget"
                    className="px-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" x2="15.42" y1="13.51" y2="17.49" />
                      <line x1="15.41" x2="8.59" y1="6.51" y2="10.49" />
                    </svg>
                  </Button>
                )}
                <Link to={`/budgets/${budget.id}/edit`}>
                  <Button variant="outline" size="sm" title="Edit budget" aria-label="Edit budget" className="px-2 md:px-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="md:mr-1"
                    >
                      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                      <path d="m15 5 4 4" />
                    </svg>
                    <span className="hidden md:inline">Edit</span>
                  </Button>
                </Link>
                {canDelete && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(true)}
                    title="Delete budget"
                    aria-label="Delete budget"
                    className="px-2 md:px-3"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="md:mr-1"
                    >
                      <path d="M3 6h18" />
                      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                    </svg>
                    <span className="hidden md:inline">Delete</span>
                  </Button>
                )}
              </>
            )}
          </div>
        )}
        </div>
      </div>

      <BudgetOverviewSection
        budget={budget}
        parentBudget={parentBudget}
        fxRateLoading={fxRateLoading}
        convertedAmountToParent={convertedAmountToParent}
        effectivePercentSpent={effectivePercentSpent}
        effectiveRemaining={effectiveRemaining}
        unallocated={unallocated}
        totalRolloverAmount={totalRolloverAmount}
        rolloverChildren={rolloverChildren}
        ownRolloverAmount={ownRolloverAmount}
        budgetAmountWithChildRollover={budgetAmountWithChildRollover}
        onOpenBudget={(budgetId) => navigate(`/budgets/${budgetId}`)}
        onScrollToTransactions={() => {
          const el = document.getElementById('budget-transactions');
          el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
        onTransactionDrop={
          canEdit ? (txId) => handleTransactionDrop(txId, budget.id) : undefined
        }
      />

      {/* Child budgets + Transactions: side-by-side on desktop */}
      <div className="md:grid md:grid-cols-2 md:gap-6 md:items-start space-y-6 md:space-y-0">
        {/* Left column: Child budgets tree */}
        <section className="flex flex-col md:min-h-0 min-w-0">
          <div className="flex items-center justify-between mb-3 gap-2 min-w-0">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-foreground">Child Budgets</h2>
              {selectionMode && (
                <p className="text-xs text-muted-foreground">
                  Selection mode – {selectedIds.length} selected
                </p>
              )}
            </div>
            {selectionMode ? (
              <Button size="sm" variant="outline" onClick={exitSelectionMode}>
                Cancel
              </Button>
            ) : (
              canEdit && (
                <Link to={`/budgets/new?parentId=${budget.id}`}>
                  <Button size="sm" variant="outline" title="Add child budget" aria-label="Add child budget" className="px-2 md:px-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="md:mr-1"
                    >
                      <path d="M5 12h14" />
                      <path d="M12 5v14" />
                    </svg>
                    <span className="hidden md:inline">Add Child</span>
                  </Button>
                </Link>
              )
            )}
          </div>

          <div className="md:overflow-y-auto md:max-h-[calc(100vh-16rem)] md:pr-1 min-w-0">
            {budget.children?.length ? (
              <div className="space-y-2">
                {sortBudgetsByExecution(budget.children).map((child: Budget) => (
                  <BudgetTreeNode
                    key={child.id}
                    node={child}
                    depth={0}
                    parentCurrency={budget.currency}
                    onTransactionDrop={canEdit ? handleTransactionDrop : undefined}
                    selectionMode={selectionMode}
                    selectedIds={selectedIds}
                    onToggleSelect={handleToggleSelect}
                    onEnterSelection={handleEnterSelection}
                    canEdit={canEdit}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-4 text-muted-foreground">
                  No child budgets yet.
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        {/* Right column: Transactions – own scroll on desktop */}
        <section id="budget-transactions" className="flex flex-col md:min-h-0 min-w-0 scroll-mt-24">
          <TransactionsSectionHeader
            canEdit={canEdit}
            hasChildren={hasChildren}
            includeChildTransactions={includeChildTransactions}
            onIncludeChildTransactionsChange={setIncludeChildTransactions}
            isObligation={isObligation}
            isObligationPaid={isObligationPaid}
            onMarkPaid={handleMarkPaid}
            onAddTransaction={() => setShowAddTransaction(true)}
            onAddRecurringSpends={() => setShowRecurringDialog(true)}
            canSelect={canSelectTransactions}
            selectionMode={txSelectionMode}
            selectedCount={selectedTxIds.length}
            onEnterSelection={() => handleEnterTxSelection()}
            onExitSelection={exitTxSelectionMode}
          />

          {/* 16rem ≈ header + overview section heights; keeps transactions scroll independent */}
          <div className="md:overflow-y-auto md:max-h-[calc(100vh-16rem)] md:pr-1 mt-3 min-w-0">
            {aggregatedTransactionsLoading ? (
              <Card>
                <CardContent className="p-6 text-center text-muted-foreground">
                  Loading transactions...
                </CardContent>
              </Card>
            ) : displayTransactions.length === 0 ? (
              <NoTransactionsEmpty
                onCreateClick={
                  canEdit
                    ? isObligation && !isObligationPaid
                      ? handleMarkPaid
                      : () => setShowAddTransaction(true)
                    : undefined
                }
              />
            ) : (
              <TransactionList
                transactions={displayTransactions}
                onTransactionClick={(t) => navigate(`/transactions/${t.id}`)}
                currentBudgetId={budget.id}
                editable={canEdit}
                draggable={canEdit && !txSelectionMode}
                onTransactionContextMenu={
                  canEdit || canSelectTransactions ? handleTransactionContextMenu : undefined
                }
                selectionMode={txSelectionMode}
                selectedIds={selectedTxIds}
                onToggleSelect={handleToggleTxSelect}
              />
            )}
          </div>
        </section>
      </div>

      {/* Transaction context menu */}
      {transactionContextMenu && (
        <div
          className="fixed inset-0 z-50"
          onClick={() => setTransactionContextMenu(null)}
          onContextMenu={(e) => {
            e.preventDefault();
            setTransactionContextMenu(null);
          }}
        >
          <div
            className="absolute min-w-[180px] rounded-lg border border-border bg-background shadow-lg py-1"
            style={clampMenuPosition(transactionContextMenu.x, transactionContextMenu.y)}
            onClick={(e) => e.stopPropagation()}
          >
            {canSelectTransactions && !txSelectionMode && (
              <button
                type="button"
                data-testid="transaction-context-select"
                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted text-left"
                onClick={() => {
                  handleEnterTxSelection(transactionContextMenu.transaction.id);
                  setTransactionContextMenu(null);
                }}
              >
                Select
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                data-testid="transaction-context-move"
                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted text-left"
                onClick={() => {
                  setMoveTransactionTarget(transactionContextMenu.transaction);
                  setTransactionContextMenu(null);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14" />
                  <path d="M12 5v14" />
                </svg>
                Move to budget
              </button>
            )}
          </div>
        </div>
      )}

      <MoveTransactionDialog
        open={!!moveTransactionTarget}
        onOpenChange={(open) => {
          if (!open) setMoveTransactionTarget(null);
        }}
        transaction={moveTransactionTarget}
        rootBudget={budget}
        onMove={handleMoveTransaction}
        isMoving={moveTransaction.isPending}
      />

      {/* Add transaction dialog */}
      <Dialog open={showAddTransaction} onOpenChange={setShowAddTransaction}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Transaction</DialogTitle>
          </DialogHeader>
          <TransactionForm
            budget={budget}
            budgets={budgets}
            selectedTagIds={selectedTagIdsForCreate}
            onTagsChange={setSelectedTagIdsForCreate}
            onSubmit={handleAddTransaction}
            onCancel={() => setShowAddTransaction(false)}
            isLoading={createTransaction.isPending || attachTag.isPending}
          />
        </DialogContent>
      </Dialog>

      {budget && (
        <RecurringTransactionDialog
          open={showRecurringDialog}
          onOpenChange={setShowRecurringDialog}
          budget={budget}
          currencies={getCommonCurrencies()}
          onSubmit={handleSubmitRecurring}
        />
      )}

      <CopyBudgetDialog
        open={showCopyDialog}
        onOpenChange={setShowCopyDialog}
        sourceBudget={budget}
      />

      {budget && !budget.parentId && (
        <BudgetShareDialog
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
          budgetId={budget.id}
          budgetName={budget.name}
        />
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={showDeleteConfirm}
        onOpenChange={(open) => {
          setShowDeleteConfirm(open);
          if (!open) setDeleteConfirmText('');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Budget</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{budget.name}"? This action cannot
              be undone and will remove all associated transactions.
              {hasChildren && (
                <span className="block mt-2 text-destructive">
                  This budget has {childCount} child budget{childCount === 1 ? '' : 's'}. Deleting it will also delete all of its children.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {hasChildren && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Type <span className="font-semibold text-foreground">{CONFIRM_WORD}</span> to confirm.
              </p>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={CONFIRM_WORD}
                autoFocus
              />
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              loading={deleteBudget.isPending}
              disabled={hasChildren && deleteConfirmText.trim() !== CONFIRM_WORD}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selectionMode && selectedBudgets.length > 0 && (
        <SelectionSummaryRibbon
          selectedBudgets={selectedBudgets}
          onExit={exitSelectionMode}
        />
      )}
      {txSelectionMode && selectedTransactions.length > 0 && (
        <TransactionSelectionSummaryRibbon
          selectedTransactions={selectedTransactions}
          onExit={exitTxSelectionMode}
        />
      )}
    </div>
  );
}

function BudgetTreeNode({
  node,
  depth = 0,
  parentCurrency,
  onTransactionDrop,
  selectionMode = false,
  selectedIds = [],
  onToggleSelect,
  onEnterSelection,
  canEdit = false,
}: {
  node: Budget;
  depth?: number;
  parentCurrency?: string;
  onTransactionDrop?: (transactionId: string, targetBudgetId: string) => void;
  selectionMode?: boolean;
  selectedIds?: string[];
  onToggleSelect?: (budgetId: string) => void;
  onEnterSelection?: (budgetId: string) => void;
  canEdit?: boolean;
}) {
  const indent = depth * 16;
  const hasChildren = (node.children?.length ?? 0) > 0;

  const isSubtreeCompleted = React.useCallback((current: Budget): boolean => {
    const remaining = current.remaining ?? (current.amount - (current.spent ?? 0));
    if (remaining !== 0) return false;
    return (current.children ?? []).every((child) => isSubtreeCompleted(child));
  }, []);

  const shouldCollapseByDefault = hasChildren && (node.children ?? []).every((child) => isSubtreeCompleted(child));
  const [isExpanded, setIsExpanded] = React.useState(!shouldCollapseByDefault);

  React.useEffect(() => {
    setIsExpanded(!shouldCollapseByDefault);
  }, [shouldCollapseByDefault, node.id]);

  const needsConversion = !!parentCurrency && parentCurrency !== node.currency;
  const { data: rate } = useFxRate(needsConversion ? node.currency : '', needsConversion ? parentCurrency ?? '' : '');

  const secondaryAmounts = React.useMemo(() => {
    if (!needsConversion || !rate || !parentCurrency) {
      return undefined;
    }
    const spent = node.spent ?? 0;
    const remaining = node.remaining ?? (node.amount - spent);
    return {
      spent: spent * rate,
      remaining: remaining * rate,
      budget: node.amount * rate,
    };
  }, [needsConversion, node.amount, node.remaining, node.spent, parentCurrency, rate]);

  const sortedChildren = React.useMemo(
    () => sortBudgetsByExecution(node.children ?? []),
    [node.children]
  );

  return (
    <div className="space-y-2 min-w-0">
      <div className="flex gap-3 min-w-0">
        <div
          className="relative shrink-0"
          style={{ width: 16, marginLeft: indent }}
          aria-hidden="true"
        >
          {depth > 0 && <span className="block h-full w-px bg-border" />}
        </div>
        <div className="flex-1 min-w-0">
          <BudgetCard
            budget={node}
            titleEditable={false}
            secondaryCurrency={needsConversion ? parentCurrency : undefined}
            secondaryAmounts={secondaryAmounts}
            actions={
              <ChildBudgetActionsMenu
                budgetId={node.id}
                budgetName={node.name}
                selectionMode={selectionMode}
                canEdit={canEdit}
                onEnterSelection={
                  onEnterSelection ? () => onEnterSelection(node.id) : undefined
                }
              />
            }
            onToggle={() => setIsExpanded((prev) => !prev)}
            expanded={isExpanded}
            onTransactionDrop={onTransactionDrop ? (txId) => onTransactionDrop(txId, node.id) : undefined}
            selectionMode={selectionMode}
            isSelected={selectedIds.includes(node.id)}
            onToggleSelect={onToggleSelect ? () => onToggleSelect(node.id) : undefined}
          />
        </div>
      </div>
      {hasChildren ? (
        <div
          className={cn(
            'grid transition-[grid-template-rows,opacity] duration-200 ease-out',
            isExpanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0 pointer-events-none invisible'
          )}
          aria-hidden={!isExpanded}
          data-testid={isExpanded ? `budget-tree-expanded-${node.id}` : `budget-tree-collapsed-${node.id}`}
        >
          <div className="overflow-hidden min-h-0">
            <div className="ml-6 space-y-2 pt-2">
              {sortedChildren.map((child: Budget) => (
                <BudgetTreeNode
                  key={child.id}
                  node={child}
                  depth={depth + 1}
                  parentCurrency={node.currency}
                  onTransactionDrop={onTransactionDrop}
                  selectionMode={selectionMode}
                  selectedIds={selectedIds}
                  onToggleSelect={onToggleSelect}
                  onEnterSelection={onEnterSelection}
                  canEdit={canEdit}
                />
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ChildBudgetActionsMenu({
  budgetId,
  budgetName,
  selectionMode = false,
  canEdit = false,
  onEnterSelection,
}: {
  budgetId: string;
  budgetName: string;
  selectionMode?: boolean;
  canEdit?: boolean;
  onEnterSelection?: () => void;
}) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!menuOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  if (selectionMode) return null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-label="Budget actions"
        aria-expanded={menuOpen}
        title={`Actions for ${budgetName}`}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setMenuOpen((open) => !open);
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        </svg>
      </button>
      {menuOpen && (
        <div
          className="absolute right-0 top-full z-40 mt-1 min-w-[140px] rounded-lg border border-border bg-background py-1 shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          {onEnterSelection && (
            <button
              type="button"
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-left hover:bg-muted"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setMenuOpen(false);
                onEnterSelection();
              }}
            >
              Select
            </button>
          )}
          {canEdit && (
            <Link
              to={`/budgets/${budgetId}/edit`}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-left hover:bg-muted"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(false);
              }}
            >
              Edit
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function getExecutionOrder(budget: Budget): number {
  const remaining = budget.remaining ?? (budget.amount - (budget.spent ?? 0));
  if (remaining > 0) return 0;
  if (remaining < 0) return 1;
  return 2;
}

function sortBudgetsByExecution(budgets: Budget[]): Budget[] {
  return [...budgets].sort((a, b) => {
    const orderDiff = getExecutionOrder(a) - getExecutionOrder(b);
    if (orderDiff !== 0) return orderDiff;
    return a.name.localeCompare(b.name);
  });
}
